import React, { PureComponent } from 'react';
import { HotspotDto, TvRefLocation } from 'app/model/CORE';
import { Flex } from 'app/components/common/Flex';
import { CheckCircleFilled, FileFilled, MoreOutlined } from '@ant-design/icons';
import { EntryLoadBar } from 'app/components/dashboard/network/flows/svg/EntryLoadBar';
import { OccupancyLoadBar } from 'app/components/dashboard/network/flows/svg/OccupancyLoadBar';
import './HotspotLoad.less';
import AppContext from 'app/common/context/AppContext';
import { HotspotBar } from 'app/components/dashboard/network/flows/svg/HotspotBar';
import { ExtendedMonitoredTv, MonitoredEvent, MonitoredMeasure } from 'app/model/SOL49';
import { PopoverMenu } from 'app/components/common/PopoverMenu';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { OpenTvMonitoredEvents, ShowModalEvent } from 'app/common/eventbus/Events';
import { HotspotPanel } from 'app/components/panels/measure/HotspotPanel';
import { MonitoredEventType, MonitoredEventUtils } from 'app/common/util/MonitoredEventUtils';
import Utils from 'app/common/util/Utils';
import ConditionalRender from 'app/common/components/ConditionalRender';

export interface HotspotLoadProps {
    tv: string;
    hotspots: HotspotDto[];
    monitoredTv: ExtendedMonitoredTv;
    monitoredEvents?: MonitoredEvent[];
    measures: MonitoredMeasure[];
    selected: boolean;

    onSelectionChange?(selected: boolean): void;

    currentTime: Date;
}

export class HotspotLoad extends PureComponent<HotspotLoadProps> {
    render() {
        const monitoredPeriod = AppContext.getTodayPeriod();
        const className = 'hotspot on-hover' + (this.props.selected ? ' selected' : '');
        const selectorClassName = (this.props.selected !== undefined ? 'show-on-hover' : 'hide') + ' check-circle';

        return (
            <Flex className={className} direction="vertical" onClick={this.handleSelectionChange}>
                <Flex>
                    <div className={selectorClassName}>
                        <CheckCircleFilled />
                    </div>
                    <Flex className="tv-id">
                        {this.props.tv}
                        <ConditionalRender condition={Utils.isNotEmpty(this.props.monitoredEvents)}>
                            <FileFilled style={{ paddingLeft: '0.5em' }} onClick={this.handleOpenEvent} />
                        </ConditionalRender>
                    </Flex>
                </Flex>
                <Flex className={'hotspot-load-bar'}>
                    <Flex direction="vertical">
                        {this.props.monitoredTv?.loadValues && (
                            <EntryLoadBar
                                key="entry-load"
                                loadValues={this.props.monitoredTv.loadValues}
                                monitoredPeriod={monitoredPeriod}
                                activePeriods={this.props.monitoredTv.activePeriods}
                                currentTime={this.props.currentTime}
                                drawBottomLine={true}
                                barHeightEm={0.5}
                            />
                        )}
                        {this.props.monitoredTv?.occupancyLoadValues &&
                            this.props.monitoredTv?.occupancyLoadValues.map(occupancyValues => {
                                return (
                                    <OccupancyLoadBar
                                        key={'occupancy-load-' + occupancyValues.durationInMin}
                                        loadValues={occupancyValues.occupancyLoadValues}
                                        monitoredPeriod={monitoredPeriod}
                                        activePeriods={this.props.monitoredTv.activePeriods}
                                        currentTime={this.props.currentTime}
                                        drawBottomLine={true}
                                        barHeightEm={0.5}
                                    />
                                );
                            })}
                        {this.props.hotspots.map((hotspot, idx) => (
                            <HotspotBar
                                key={`hotspot-bar-${idx}`}
                                barHeight={1}
                                hotspot={hotspot}
                                monitoredPeriod={monitoredPeriod}
                            />
                        ))}
                    </Flex>
                    <div className={'more-button ' + selectorClassName} onClick={e => e.stopPropagation()}>
                        <HotspotMoreMenu
                            tvId={this.props.tv}
                            refLoc={this.props.monitoredTv?.tvRefLocation}
                            measures={this.props.measures}
                            events={this.props.monitoredEvents}
                        />
                    </div>
                </Flex>
            </Flex>
        );
    }

    handleOpenEvent = (event: React.MouseEvent) => {
        event.stopPropagation();
        const measureIds = this.props.measures?.map(m => m.measure.measureId.id);
        OpenTvMonitoredEvents.fire(
            MonitoredEventUtils.getType(this.props.monitoredEvents?.[0]),
            this.props.tv,
            this.props.monitoredTv?.tvRefLocation,
            undefined,
            measureIds,
        );
    };

    handleSelectionChange = () => {
        // If selected is undefined, it means that the component cannot be selected
        this.props.onSelectionChange?.(!this.props.selected);
    };
}

interface HotspotMoreMenuProps {
    tvId: string;
    refLoc: TvRefLocation;
    events: MonitoredEvent[];
    measures: MonitoredMeasure[];
}

class HotspotMoreMenu extends PureComponent<HotspotMoreMenuProps> {
    handleMoreMenuItemClick = (key: string) => {
        const measureIds = this.props.measures?.map(m => m.measure.measureId.id);
        switch (key) {
            case 'open-edit':
                ShowModalEvent.fireNotClosable(
                    <HotspotPanel tv={this.props.tvId} />,
                    HotspotPanel.HOTSPOT_PANEL_WIDTH_EM,
                );
                break;
            case 'notes':
                OpenTvMonitoredEvents.fire(
                    MonitoredEventType.NOTE,
                    this.props.tvId,
                    this.props.refLoc,
                    undefined,
                    measureIds,
                );
                break;
            case 'events':
                OpenTvMonitoredEvents.fire(
                    MonitoredEventType.WEATHER_EVENT,
                    this.props.tvId,
                    this.props.refLoc,
                    undefined,
                    measureIds,
                );
                break;
        }
    };

    render() {
        const notes = this.props.events?.filter(MonitoredEventUtils.isNoteEvent);
        const events = this.props.events?.filter(MonitoredEventUtils.isWeatherEvent);

        const menuItems: ItemType[] = [];
        menuItems.push({ key: 'open-edit', label: `Open/Edit hotspot` });
        menuItems.push(
            { key: 'divider-1', type: 'divider' },
            { key: 'notes', label: `${Utils.isNotEmpty(notes) ? 'Show notes' : 'Add a note'}` },
            { key: 'events', label: `${Utils.isNotEmpty(events) ? 'Show events' : 'Add an event'}` },
        );
        return (
            <PopoverMenu onMenuItemClick={this.handleMoreMenuItemClick} menuItems={menuItems}>
                <MoreOutlined />
            </PopoverMenu>
        );
    }
}
